import _ from 'lodash'
import type {
	CompGridData,
	GridOverflow,
	GridVariant,
	MeshDataOptions,
	MeshMarginsOptions,
	MeshStyle,
	VerticalArea,
	MeshContainerContentArea,
	MeshContainerLayout,
	MeshItemLayout,
	SingleLayoutData,
} from '@wix/thunderbolt-becky-types'

import { bottom, fitToContentHeight, left, top } from './mesh3Utils'
import { sizeToString } from '@wix/thunderbolt-commons'
import { getComponentBoundingBox } from './mesh3BoundingLayout'

const isIE = (cssGridVariant: GridVariant) => cssGridVariant === 'ms'

export const unitize = (n: number | string) => (_.isString(n) ? n : `${n}px`)

const getContentAreaAlignmentNumber = (alignment: MeshContainerContentArea['alignment']): number => {
	if (alignment === 'start') {
		return 0
	}
	if (alignment === 'center') {
		return 0.5
	}

	// alignment === 'end'
	return 1
}

const getContentAreaWidthCss = (contentArea: MeshContainerContentArea): string => {
	const contentAreaWidth = `(${sizeToString(contentArea.width)} - var(--site-width-delta, 0px))`
	return _.isUndefined(contentArea.ratio) ? contentAreaWidth : `${contentAreaWidth} * ${contentArea.ratio}`
}

const getContentAreaLeftCss = (contentArea: MeshContainerContentArea): string => {
	const alignmentNum = getContentAreaAlignmentNumber(contentArea.alignment)
	return `calc((100% - ${getContentAreaWidthCss(contentArea)}) * ${alignmentNum})`
}

export const getContainerStyle = (containerSingleLayoutData: SingleLayoutData): MeshStyle => {
	const meshContainerLayout = containerSingleLayoutData.containerLayout as MeshContainerLayout

	return {
		height: 'auto',
		...(meshContainerLayout?.contentArea && {
			[CONTENT_AREA_LEFT_VAR_NAME]: getContentAreaLeftCss(meshContainerLayout?.contentArea),
		}),
	}
}

export const getRootContainerStyle = (containerSingleLayoutData: SingleLayoutData): MeshStyle => {
	const contentArea = (containerSingleLayoutData.containerLayout as MeshContainerLayout).contentArea

	return {
		...(contentArea && {
			minWidth: `calc(${getContentAreaWidthCss(contentArea)})`,
		}),
	}
}

const overflowDirectionToMarginKey: { top: 'marginTop'; bottom: 'marginBottom' } = {
	top: 'marginTop',
	bottom: 'marginBottom',
}

const getOverflowGridMargins = (gridOverflow: VerticalArea): MeshStyle =>
	_(gridOverflow)
		.pickBy((distance: number) => distance > 0)
		// @ts-ignore
		.reduce((result: MeshStyle, distance, direction: 'top' | 'bottom') => {
			// @ts-ignore
			result[overflowDirectionToMarginKey[direction]] = unitize(distance * -1)

			return result
		}, {})

const getTemplateRepeat = (numberOfRows: number): Array<string> => {
	if (numberOfRows < 2) {
		return _.times(numberOfRows, _.constant('min-content'))
	}

	return [`repeat(${numberOfRows}, min-content)`]
}

const templateRows = (numberOfRows: number, ie: boolean): MeshStyle => ({
	[ie ? '-ms-grid-rows' : 'gridTemplateRows']: getTemplateRepeat(numberOfRows - 1)
		.concat('1fr')
		.join(' '),
})

export const getGridWrapperStyle = (gridOverflow: GridOverflow, ie: boolean): MeshStyle =>
	gridOverflow || ie ? { display: ie ? '-ms-flexbox' : 'flex' } : {}

export const getGridContainerStyles = (
	cssGridVariant: GridVariant,
	numberOfRows: number,
	gridOverflow: VerticalArea,
	containerMinHeight: string
): MeshStyle => {
	const ie = isIE(cssGridVariant)

	return {
		position: 'static',
		display: ie ? '-ms-grid' : 'grid',
		height: 'auto',
		width: '100%',
		minHeight: containerMinHeight,
		...getOverflowGridMargins(gridOverflow),
		...templateRows(numberOfRows, ie),
		...{ [ie ? '-ms-grid-columns' : 'gridTemplateColumns']: '100%' },
	}
}

export const getEmptyContainerStyles = (containerMinHeight: string): MeshStyle => ({
	position: 'static',
	height: 'auto',
	width: '100%',
	minHeight: containerMinHeight,
})

const getGridArea = (rowStart: number, columnStart: number, rowEnd: number, columnEnd: number, ie: boolean) =>
	ie
		? {
				'-ms-grid-column': columnStart.toString(),
				'-ms-grid-column-span': (columnEnd - columnStart).toString(),
				'-ms-grid-row': rowStart.toString(),
				'-ms-grid-row-span': (rowEnd - rowStart).toString(),
		  }
		: {
				gridArea: [rowStart, columnStart, rowEnd, columnEnd].join(' / '),
		  }

const getMarginBottom = (
	compId: string,
	itemSingleLayoutData: SingleLayoutData,
	compGridData: CompGridData,
	gridVerticalArea: VerticalArea,
	containerSingleLayoutData: SingleLayoutData,
	{ softMargin, minLockMargin }: MeshMarginsOptions,
	// @ts-ignore
	negativeBottomTops
): string => {
	if (compGridData.bottomParent && !fitToContentHeight(containerSingleLayoutData)) {
		const distanceFromRowBottom = gridVerticalArea.bottom - bottom(itemSingleLayoutData)
		// @ts-ignore
		return unitize(distanceFromRowBottom > minLockMargin ? softMargin : distanceFromRowBottom)
	}

	// @ts-ignore
	if (negativeBottomTops[compId]) {
		// @ts-ignore
		return unitize(-1 * negativeBottomTops[compId])
	}

	return compGridData.marginBottom ? unitize(compGridData.marginBottom) : '0'
}

const CONTENT_AREA_LEFT_VAR_NAME = '--content-area-left'

const isAlignToContentArea = (itemLayout: MeshItemLayout) => {
	return !itemLayout.justifySelf || itemLayout.justifySelf === 'content'
}

const isAlignLeft = (itemLayout: MeshItemLayout) => {
	return isAlignToContentArea(itemLayout) || itemLayout.justifySelf === 'start'
}

const getMarginLeft = (itemSingleLayoutData: SingleLayoutData, containerSingleLayoutData: SingleLayoutData): string => {
	const itemLayout = itemSingleLayoutData.itemLayout as MeshItemLayout

	if (isAlignToContentArea(itemLayout)) {
		if ((containerSingleLayoutData.containerLayout as MeshContainerLayout).contentArea) {
			return `var(${CONTENT_AREA_LEFT_VAR_NAME})`
		}
	}

	return '0px'
}

export const getMarkerStyle = (contentArea: MeshContainerContentArea): MeshStyle => ({
	position: 'relative',
	left: `var(${CONTENT_AREA_LEFT_VAR_NAME})`,
	width: `calc(${getContentAreaWidthCss(contentArea)})`,
	visibility: 'hidden',
	'pointer-events': 'none',
})

export const getWedgeStyle = (wedgeGridData: CompGridData, ie: boolean): MeshStyle => ({
	visibility: 'hidden',
	// @ts-ignore
	height: unitize(wedgeGridData.height),
	width: '0',
	...getGridArea(wedgeGridData.rowStart, 1, wedgeGridData.rowEnd, 2, ie),
})

export const getCompStyles = (
	compId: string,
	itemSingleLayoutData: SingleLayoutData,
	compGridData: CompGridData,
	gridVerticalArea: VerticalArea,
	containerSingleLayoutData: SingleLayoutData,
	meshOptions: MeshDataOptions,
	// @ts-ignore
	negativeBottomTops
): MeshStyle => {
	// @ts-ignore
	const ie = isIE(meshOptions.cssGridVariant)

	const marginBottom = getMarginBottom(
		compId,
		itemSingleLayoutData,
		compGridData,
		gridVerticalArea,
		containerSingleLayoutData,
		meshOptions,
		negativeBottomTops
	)
	return {
		position: 'relative',
		marginTop: unitize(compGridData.marginTop!),
		marginBottom,
		marginLeft: getMarginLeft(itemSingleLayoutData, containerSingleLayoutData),
		left: isAlignLeft(itemSingleLayoutData.itemLayout as MeshItemLayout) ? unitize(left(itemSingleLayoutData)) : 0,
		...getGridArea(compGridData.rowStart, 1, compGridData.rowEnd, 2, ie),
		[ie ? '-ms-grid-row-align' : 'alignSelf']: 'start',
	}
}

export const getRotatedComponentWrapperStyle = (
	compId: string,
	compSingleLayoutData: SingleLayoutData,
	rotate: number,
	containerSingleLayoutData: SingleLayoutData,
	compGridData: CompGridData,
	gridVerticalArea: VerticalArea,
	meshOptions: MeshDataOptions,
	// @ts-ignore
	negativeBottomTops
): MeshStyle => {
	// @ts-ignore
	const ie = isIE(meshOptions.cssGridVariant)
	const wrapperComp = _.assign({}, compSingleLayoutData, {
		...compSingleLayoutData,
		itemLayout: {
			...compSingleLayoutData.itemLayout,
			meshData: getComponentBoundingBox(compSingleLayoutData, rotate),
		},
	}) as SingleLayoutData

	return {
		position: 'static',
		height: unitize(Math.ceil((wrapperComp.itemLayout as MeshItemLayout).meshData.height)),
		width: '0',
		marginTop: unitize(compGridData.marginTop!),
		marginBottom: getMarginBottom(
			compId,
			wrapperComp,
			compGridData,
			gridVerticalArea,
			containerSingleLayoutData,
			meshOptions,
			negativeBottomTops
		),
		marginLeft: getMarginLeft(wrapperComp, containerSingleLayoutData),
		...getGridArea(compGridData.rowStart, 1, compGridData.rowEnd, 2, ie),
		[ie ? '-ms-grid-row-align' : 'alignSelf']: 'start',
	}
}

export const getRotatedComponentStyle = (compSingleLayoutData: SingleLayoutData, rotate: number): MeshStyle => {
	const childBoundingBox = getComponentBoundingBox(compSingleLayoutData, rotate)

	return {
		position: 'relative',
		left: unitize(left(compSingleLayoutData)),
		top: unitize(top(compSingleLayoutData) - childBoundingBox.y),
	}
}

export const getCompAbsoluteStyle = (
	compSingleLayoutData: SingleLayoutData,
	containerSingleLayoutData: SingleLayoutData
): MeshStyle => ({
	position: 'absolute',
	top: unitize(top(compSingleLayoutData)),
	left: unitize(left(compSingleLayoutData)),
	marginLeft: getMarginLeft(compSingleLayoutData, containerSingleLayoutData),
})
